'use strict';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash-es';
import { BuildingTemplate } from '../../../app/resources/building-template/building-template.interface';
import { PveTemplate } from '../../../app/utilities/export/pve-template.interface';

// AppSection: Template vs Project
// project level: project, building
// Action: create, copy, edit
// TODO add if useful Element category: technical, textual or all

(function () {
  angular
    .module('specsguardApp')
    .controller('SgElementFormCtrl', SgElementFormCtrl);

  SgElementFormCtrl.$inject = ['Labels', '$timeout', 'sgElementHelpers',
  'SimpleDialogFormService', 'sg2ProjectModuleService', 'sg2ProjectSubmoduleService', 'BuildingTemplateStore', 'PveTemplateStore'];

  function SgElementFormCtrl (Labels, $timeout, sgElementHelpers,
    SimpleDialogFormService, sg2ProjectModuleService, sg2ProjectSubmoduleService, buildingTemplateStore, pveTemplateStore) {
    const vm = this;
    let elementHelper;

    vm.destroy$ = new Subject();
    vm.element = {moduleId: ''};
    //
    // Initialization
    //
    vm.$onInit = function () {
      elementHelper = new sgElementHelpers({constants: vm.constants});
      vm.hiddenFields = getHiddenFields(vm.hiddenFieldsOptional);
      vm.disabledFields = getDisabledFields(vm.disabledFieldsOptional);

      vm.LABELS = Labels;
      // keep a shallow clone so we can filter again if needed
      vm.modulesOriginal = [...vm.modules];

      vm.projectLevels = elementHelper.availableProjectLevels(vm.projectLevel, vm.appSection);

      if (vm.action === 'create') {
        vm.projectLevel = 2;
        vm.element.projectLevel = 2;
        vm.element.knowledgeLevel = 3;
        vm.element.inputType = 'Long String';
        vm.element.hasMultipleQualityLevels = false;
      }

      vm.filterInputTypes = elementHelper.filterInputTypes.bind(elementHelper);

      vm.templates = [];

      combineLatest(buildingTemplateStore.load(), pveTemplateStore.load()).pipe(
        takeUntil(vm.destroy$)
      ).subscribe(([buildingTemplates, pveTemplates]: [BuildingTemplate[], PveTemplate[]]) => {

        if (!buildingTemplates.length || !pveTemplates.length) {
          return;
        }

        vm.buildingtemplates = buildingTemplates.map((item) => ({
          _id: item._id,
          name: item.name,
          type: 'buildingtemplate'
        }));

        vm.pvetemplates = pveTemplates.map((item) => ({
          _id: item._id,
          name: item.name,
          type: 'pvetemplate'
        }));

        setTemplatesBasedOnProjectLevel();
      });

      initValues();

      // Make sure cascaded selection based on projectlevel is triggered
      vm.projectLevelChanged({projectLevel: vm.projectLevel});
    };

    // Listen to the modules observable
    sg2ProjectModuleService.state$.pipe(
      takeUntil(vm.destroy$)
    ).subscribe(modules => {
      vm.modules = modules;
    });

    // Listen to the submodules observable
    sg2ProjectSubmoduleService.state$.pipe(
      takeUntil(vm.destroy$)
    ).subscribe(submodules => {
      // Only show submodules matching the current module
      vm.submodules = submodules.filter(submodule => submodule.moduleId === vm.element.moduleId);
    });

    //
    // Internal data updates
    //
    const initValues = () => {
      setValue();
      vm.updateModules();
      vm.toggleInputTypes();

      $timeout(triggerValidation); // make sure we validate when the form is finished with setting values / rendering
    };

    const setValue = () => {
      // set potential knowledge level based on project level
      vm.filteredKnowledgeLevels = elementHelper.filterKnowledgeLevels(vm.element);
      vm.inputTypes = elementHelper.filterInputTypes({
        element: vm.element,
        hiddenFields: vm.hiddenFields,
        disableFields: vm.disabledFields
      });

      vm.element.qualityLevel = {};
      vm.element.pveTemplates = vm.element.pveTemplates || [];

      if (vm.action !== 'create') {
        const valuesLength = vm.element.values ? vm.element.values.length : 0;
        if (!vm.element.hasMultipleQualityLevels && valuesLength > 0) {
          vm.element.qualityLevel.all = vm.element.values[0].value;
        } else if (vm.element.constructionId && vm.element.value) {
          vm.element.hasMultipleQualityLevels = false;
          vm.element.qualityLevel.all = vm.element.value;
        } else if (vm.element.hasMultipleQualityLevels && valuesLength > 0) {
          vm.element.values.forEach(function (value) {
            vm.element.qualityLevel[value.quality] = value.value;
          });
        }
      }
    };

    vm.updateModules = () => {
      sg2ProjectSubmoduleService.refresh();

      // For support outside of the ProjectStores.
      if (vm.isTemplatePage()) {
        findModules();
        filterModules();
      }
    };

    /**
     * Check whether the current page is a template page.
     *
     * @todo Code below is prone to errors, so it should be tied to the ProjectService's
     *  project$ observable, to check whether there is *currently an active project*.
     *
     * @returns Whether the current page is a template page.
     */
    vm.isTemplatePage = (): boolean => {
      return window.location.href.split('/').indexOf('project') === -1;
    };

    function findModules () {
      const {module, submodule} = elementHelper.findModules(vm.modules, vm.element);
      vm.element.module = module;
      vm.element.submodule = submodule;

      // Create a copy of the submodules for the Submodule-dropdown.
      vm.submodules = get(vm.element, 'module.submodules', []);
    }

    function filterModules () {
      vm.modules = elementHelper.filterModulesOnRequirement({
        modules: vm.modules,
        originalModules: vm.modulesOriginal,
        element: vm.element,
      });
    }

    function triggerValidation () {
      if (vm.form) {
        angular.forEach(vm.form.$error.required, function (field) {
          field.$touched = true;
          field.$valid = false;
        });
      }
    }

    //
    // Internal data updates on user interactions
    //
    vm.projectLevelChanged = function (str, evtype, ev) {

      // In read only mode this change event should not run
      if (vm.action === 'view') {
        return;
      }

      // No projectlevel? no need to run change
      if (!str || !str.projectLevel) {
        return;
      }

      const _previousProjectLevel = vm._previousProjectLevel;

      if (vm.element.pveTemplates.length) {
        const options = {
          title: Labels.getLabel('confirmchange'),
          content: Labels.getLabel('projectlevelchanged'),
          ok_icon: 'delete',
          ok: Labels.getPlural('confirm')
        };

        SimpleDialogFormService.confirm(ev, options).then(() => {
          // vm.hiddenFields.hasInputInstruction = str.projectLevel === 1;
          setTemplatesBasedOnProjectLevel();
          vm.element.pveTemplates = [];
        }).catch(() => {
          vm.element.projectLevel = _previousProjectLevel;
        });
      } else {
        // vm.hiddenFields.hasInputInstruction = str.projectLevel === 1;
        setTemplatesBasedOnProjectLevel();
      }

      vm.filteredKnowledgeLevels = elementHelper.filterKnowledgeLevels(vm.element, str);

      // If the projectLevel changed we should check if the selected knowledgelevel is still
      // in the filtetered knowledge levels. If it is not we should select it
      if (!vm.filteredKnowledgeLevels.find(kl => kl._id === vm.element.knowledgeLevel)) {

        // ProjectLevel: Project
        if (str.projectLevel === 1) {
          vm.element.knowledgeLevel = 1; // Ambitie
        // ProjectLevel: Gebouw
        } else if (str.projectLevel === 2) {
          vm.element.knowledgeLevel = 3; // Eis
        }
      }
      // Should also call the knowledgeLevel change
      vm.knowledgeLevelChanged();
    };

    /**
     * On knowledge level change cascade changes to the form
     */
    vm.knowledgeLevelChanged = function () {
      if (vm.element.knowledgeLevel === 3) { // Eis
        // Check if soort eis is selected, if not select the default
        if (!vm.element.requirementType) {
          vm.element.requirementType = 1; // Functionele Eis
        }

        // Check if strictheid is selected, if not select the default
        if (!vm.element.requirementStrictness) {
          vm.element.requirementStrictness = 1; // Specificatie
        }
      }

      vm.filterInputTypes({element: vm.element, disabledFields: vm.disabledFields, hiddenFields: vm.hiddenFields, reset: true});
    };

    function setTemplatesBasedOnProjectLevel() {
      switch (vm.element.projectLevel) {
        case 1: // Project
          vm.templates = vm.pvetemplates;
          break;
        case 2: // Building
          vm.templates = vm.buildingtemplates;
          break;
        default:
          vm.templates = [];
          break;
      }
    }

    vm.changeRequirementType = () => {
      // filterModules();
      vm.toggleInputTypes();
    };

    vm.toggleInputTypes = () => {
      const {inputType, disabled} = elementHelper.toggleInputTypeOnRequirement({
        element: vm.element,
        disabledFields: vm.disabledFields
      });
      vm.element.inputType = inputType;
      vm.disabledFields = disabled;
    };

    vm.resetValues = () => {
      if (vm.element.qualityLevel.all) {
        vm.element.values.forEach(function (value) {
          vm.element.qualityLevel[value.quality] = vm.element.qualityLevel.all;
         });
        delete vm.element.qualityLevel.all;
      } else {
        vm.element.qualityLevel.all = vm.element.qualityLevel[vm.element.values[0].quality];
        vm.element.values.forEach(function (value) {
          if (value.quality !== "all") {
            delete vm.element.qualityLevel[value.quality];
          }
         });
      }
    };

    vm.selectInputType = () => {
      const {knowledgeLevel, inputType} = vm.element;

      vm.resetValues();
    };

    vm.exists = (id) => vm.element.appliesToFases && vm.element.appliesToFases.indexOf(id);

    vm.appliesToFase = (fase) => {
      const faseIndex = vm.exists(fase._id);
      if (faseIndex === -1) {
        vm.element.appliesToFases.splice(fase._id - 1, 1, fase._id);
      } else {
        vm.element.appliesToFases.splice(faseIndex, 1, null);
      }
    };

    //
    // Data updates from parents
    //
    vm.$onChanges = function (changesObj) {
      if (changesObj.element && !changesObj.element.isFirstChange()) {
        initValues();
      }
    };

    // needed when an element is edited/created. When reopening the modal,
    // it starts out empty again if needed. reset() is called from parent.
    vm.reset = () => {
      Object.keys(vm.element).forEach((key) => {
        if (angular.isDefined(emptyElement[key])) {
          vm.element[key] = emptyElement[key];
        } else if (!vm.disabledFields[key] && !vm.hiddenFields[key]) {
          delete vm.element[key];
        }
      });

      initValues();
    };

    // Returns unselected templates
    vm.getAvailableTemplates = () => vm.templates.filter(function (current) {
      return vm.element.pveTemplates.filter(function (other) {
        return other._id === current._id;
      }).length === 0;
    });

    //
    // Helpers
    //
    vm.filteredConstructions = () => {
      const constructions = vm.constructions || [];
      return constructions.filter((c) => c.projectLevel === vm.element.projectLevel);
    };

    const emptyElement = {
      hasInputInstruction: false,
      hasMultipleQualityLevels: false,
      appliesToFases: [1, 2, 3, 4],
      values: [],
      unit: 'none',
    };

    vm.checkValueFilledIn = () => {
      if (angular.isUndefined(vm.element.qualityLevel)) {
        return false;
      }

      return Object.keys(vm.element.qualityLevel).some(function (key) {
        return vm.element.qualityLevel[key] !== null && angular.isDefined(vm.element.qualityLevel[key]);
      });
    };

    // Wrapping these in functions so they are initialized on $onInit,
    // otherwise vm.appSection === undefined
    const getHiddenFields = (fieldsArr = []) => {
      const defaultFields = {
        name: false,
        label: false,
        projectLevel: false,
        constructionId: true,
        moduleId: false,
        submoduleId: false,
        knowledgeLevel: false,
        requirementType: false,
        requirementStrictness: false,
        inputType: true,
        hasMultipleQualityLevels: false,
        unit: true,
        logAndComments: vm.action === 'create',
        appliesToFases: true,
        pveTemplates: false
      };

      fieldsArr.forEach((key) => defaultFields[key] = true);

      return defaultFields;
    };

    /**
     * Create new module.
     */
    vm.createModule = (): void => {
      if (vm.isTemplatePage()) {
        // Create new module on template page.
      } else {
        sg2ProjectModuleService.create({
          projectId: vm.element.projectId,
          isTechnicalType: false
        }).subscribe(createdModule => {
          vm.element.moduleId = createdModule._id;
          sg2ProjectSubmoduleService.refresh();
        });
      }
    };

    /**
     * Create new submodule.
     */
    vm.createSubmodule = (): void => {
      if (vm.isTemplatePage()) {
        // Create new submodule on template page.
      } else {
        sg2ProjectSubmoduleService.create({
          projectId: vm.element.projectId,
          moduleId: vm.element.moduleId
        }).subscribe(createdSubmodule => {
          vm.element.submoduleId = createdSubmodule._id;
        });
      }
    };

    const getDisabledFields = (fieldsArr = []) => {
      const defaultFields = {
        name: false,
        label: false,
        moduleId: false,
        submoduleId: false,
        knowledgeLevel: false,
        requirementType: false,
        requirementStrictness: false,
        hasInputInstruction: false,
        inputInstruction: false,
        inputType: true,
        hasMultipleQualityLevels: false,
        unit: true,
        comments: false,
        appliesToFases: false,
        pveTemplates: vm.action === 'view'
      };

      fieldsArr.forEach((key) => defaultFields[key] = true);

      return defaultFields;
    };
  }
})();
